import { z } from 'zod';

/**
 * The Zoho Books migration contract. Same three steps as the CRM one —
 * analyze, dry run, commit — and the same promise: the dry run runs the
 * identical pipeline as the commit inside a transaction that is rolled back.
 */

export interface BooksEntityInfo {
  entity: string;
  label: string;
  /** Export file stems (normalised) that hold this entity. */
  stems: readonly string[];
  /** Import order. A later entity may look up an earlier one. */
  order: number;
  /** Which Zoho product's backup the file comes from; file names repeat across them. */
  source: BooksSource;
}

export const BOOKS_SOURCES = ['books', 'expense'] as const;
export type BooksSource = (typeof BOOKS_SOURCES)[number];

export const BOOKS_ENTITIES = [
  { entity: 'customers', label: 'Customers', stems: ['contacts'], order: 10, source: 'books' },
  {
    entity: 'contactPersons',
    label: 'Customer contacts',
    stems: ['contact_persons'],
    order: 12,
    source: 'books',
  },
  { entity: 'vendors', label: 'Vendors', stems: ['vendors'], order: 15, source: 'books' },
  { entity: 'items', label: 'Items', stems: ['item'], order: 20, source: 'books' },
  {
    entity: 'chartOfAccounts',
    label: 'Chart of accounts',
    stems: ['chart_of_accounts'],
    order: 25,
    source: 'books',
  },
  { entity: 'quotes', label: 'Quotes', stems: ['quote'], order: 30, source: 'books' },
  {
    entity: 'salesOrders',
    label: 'Sales orders',
    stems: ['sales_order'],
    order: 35,
    source: 'books',
  },
  { entity: 'invoices', label: 'Invoices', stems: ['invoice'], order: 40, source: 'books' },
  {
    entity: 'customerPayments',
    label: 'Payments received',
    stems: ['customer_payment'],
    order: 50,
    source: 'books',
  },
  {
    entity: 'creditNotes',
    label: 'Credit notes',
    stems: ['credit_note'],
    order: 60,
    source: 'books',
  },
  {
    entity: 'creditNoteApplications',
    label: 'Credit notes applied to invoices',
    stems: ['creditnotes_invoice'],
    order: 62,
    source: 'books',
  },
  {
    entity: 'creditNoteRefunds',
    label: 'Credit note refunds',
    stems: ['refund'],
    order: 64,
    source: 'books',
  },
  { entity: 'expenses', label: 'Expenses', stems: ['expense'], order: 70, source: 'books' },
  { entity: 'journals', label: 'Manual journals', stems: ['journal'], order: 80, source: 'books' },
  // Zoho Expense. Its files are named like Books' (Expense.csv, Contacts.csv, …),
  // so a file is only matched to these when the upload says it is from Expense.
  {
    entity: 'expenseReports',
    label: 'Expense reports',
    stems: ['expense_report'],
    order: 40,
    source: 'expense',
  },
  {
    entity: 'standaloneExpenses',
    label: 'Expenses outside a report',
    stems: ['expense'],
    order: 45,
    source: 'expense',
  },
] as const satisfies readonly BooksEntityInfo[];

export type BooksEntity = (typeof BOOKS_ENTITIES)[number]['entity'];
export const BOOKS_ENTITY_NAMES = BOOKS_ENTITIES.map((entry) => entry.entity) as [
  BooksEntity,
  ...BooksEntity[],
];

/** Export files that are recognised but not imported, with the reason shown. */
export const BOOKS_SKIPPED_FILES: Record<string, string> = {
  activity_logs: 'The activity log is not imported.',
  bill: 'Bills are not imported in this version.',
  bill_of_entry: 'Bills of entry are not imported.',
  budget: 'Budgets are not imported in this version.',
  delivery_challan: 'Delivery challans are not imported.',
  deposit: 'Bank deposits are not imported in this version.',
  exchange_rate: 'Exchange rates are not imported.',
  fixed_asset: 'Fixed assets are not imported.',
  inventory_adjustment: 'Inventory adjustments are not imported.',
  price_lists: 'Price lists are not imported.',
  project_tasks: 'Projects and timesheets are not imported.',
  projects: 'Projects and timesheets are not imported.',
  timesheet: 'Projects and timesheets are not imported.',
  purchase_order: 'Purchase orders are not imported in this version.',
  purchase_price_lists: 'Price lists are not imported.',
  recurring_bill: 'Recurring bills are not imported.',
  recurring_expense: 'Recurring expenses are not imported in this version.',
  recurring_invoice: 'Recurring invoices are not imported in this version.',
  replenishment: 'Replenishment rules are not imported.',
  sales_manager_report: 'Custom report data is not imported.',
  sales_receipt: 'Sales receipts are not imported in this version.',
  task: 'Tasks are not imported.',
  tax: 'Taxes are set per line on each document.',
  tax_authority: 'Tax settings are not imported.',
  tax_exemption: 'Tax settings are not imported.',
  tax_group: 'Tax settings are not imported.',
  transfer_fund: 'Fund transfers are not imported in this version.',
  vendor_contact_persons: 'Vendor contact persons are not imported.',
  vendor_credits: 'Vendor credits are not imported in this version.',
  vendor_credits_refund: 'Vendor credits are not imported in this version.',
  vendor_payment: 'Vendor payments are not imported in this version.',
};

/** Zoho Expense files that are recognised but not imported, with the reason shown. */
export const EXPENSE_SKIPPED_FILES: Record<string, string> = {
  chart_of_accounts: 'The chart of accounts comes from Zoho Books.',
  contacts: 'Customers come from Zoho Books.',
  department: 'Departments are not imported.',
  employee_advance: 'Employee advances are not imported in this version.',
  employee_reimbursement: 'Reimbursements are carried on each report.',
  expense_category: 'Categories are carried on each expense.',
  merchant: 'Merchants are carried on each expense.',
  projects: 'Projects are not imported.',
  purchase_request: 'Purchase requests are not imported.',
  trip: 'Trips are not imported.',
  users: 'People are matched to your users by email.',
  vendor_payment: 'Report approval history is not imported.',
};

export const booksFileSchema = z.object({
  name: z.string().min(1),
  entity: z.enum(BOOKS_ENTITY_NAMES),
  csv: z.string().min(1),
});
export type BooksFile = z.infer<typeof booksFileSchema>;

export const booksImportOptionsSchema = z.object({
  /** Business manager for documents whose Books owner has no user here. */
  fallbackOwnerId: z.uuid().optional(),
});

/** Per-entity overrides: importer column → the file column to read it from ('' to ignore). */
export const booksMappingSchema = z.partialRecord(
  z.enum(BOOKS_ENTITY_NAMES),
  z.record(z.string(), z.string()),
);
export type BooksMapping = z.infer<typeof booksMappingSchema>;

export const booksRunSchema = z.object({
  files: z.array(booksFileSchema).min(1, 'Upload at least one file'),
  /** Per-entity column overrides from the mapping step. */
  mapping: booksMappingSchema.optional(),
  options: booksImportOptionsSchema.optional(),
  /** Entities to include. Omitted, every entity in the upload. */
  entities: z.array(z.enum(BOOKS_ENTITY_NAMES)).optional(),
});
export type BooksRunInput = z.infer<typeof booksRunSchema>;

export interface BooksEntityAnalysis {
  entity: BooksEntity;
  label: string;
  fileName: string;
  /** Rows in the file. */
  rows: number;
  /** Records those rows make (an invoice is several rows). */
  records: number;
  /** Problems visible before anything is written. */
  issues: number;
  /** The file's own header row, for the mapping dropdowns. */
  headers: string[];
  /** What each importer column reads from this file by default. */
  columns: BooksColumnStatus[];
  unusedHeaders: string[];
  /** Required columns the file does not supply. The entity cannot import until mapped. */
  missingRequired: string[];
}

export interface BooksAnalysis {
  entities: BooksEntityAnalysis[];
  skippedFiles: { name: string; reason: string }[];
  unrecognizedFiles: string[];
  importOrder: BooksEntity[];
}

/** Stem of an export file: `Sales_Order.csv` → `sales_order`. */
export function booksStem(fileName: string): string {
  const base = fileName.split('/').pop() ?? fileName;
  return base
    .replace(/\.[^.]+$/u, '')
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/gu, '_');
}

export function booksEntityForFile(
  fileName: string,
  source: BooksSource = 'books',
): BooksEntity | null {
  const stem = booksStem(fileName);
  return (
    BOOKS_ENTITIES.find(
      (entry) => entry.source === source && (entry.stems as readonly string[]).includes(stem),
    )?.entity ?? null
  );
}

// ---------------------------------------------------------------------------
// Column mapping
// ---------------------------------------------------------------------------

/**
 * A column the importer reads, under the name Zoho Books exports it with.
 *
 * The importer's staging code reads these names, so mapping is a rename: the
 * user's chosen file column is copied onto the name the importer expects
 * before anything else runs. Adding a field here without reading it does
 * nothing, and reading a column that is not listed here means it cannot be
 * remapped — the test suite keeps the two honest.
 */
export interface BooksField {
  /** The name the importer reads. */
  column: string;
  label: string;
  /** Other header spellings that mean the same thing. */
  aliases?: readonly string[];
  /** Without this the entity cannot be imported. */
  required?: boolean;
  /** At least one field of the group must be mapped (a name, from three possible columns). */
  group?: string;
}

const f = (
  column: string,
  label: string,
  extra: Partial<Omit<BooksField, 'column' | 'label'>> = {},
): BooksField => ({ column, label, ...extra });

const ADDRESS_COLUMNS: BooksField[] = [
  f('Billing Address', 'Billing address'),
  f('Billing Street2', 'Billing address line 2'),
  f('Billing City', 'Billing city'),
  f('Billing State', 'Billing state'),
  f('Billing Code', 'Billing postal code'),
  f('Billing Country', 'Billing country'),
];

const DOCUMENT_LINE_COLUMNS: BooksField[] = [
  f('Item Name', 'Line item name'),
  f('Item Desc', 'Line item description'),
  f('Item Price', 'Line item price'),
  f('Discount', 'Line discount'),
  f('Discount Amount', 'Line discount amount'),
  f('Item Tax %', 'Line tax %'),
  f('Item Tax Amount', 'Line tax amount'),
  f('Product ID', 'Item id'),
];

const documentFields = (
  id: string,
  number: string,
  date: string,
  status: string,
  options: { due?: string; subject?: string; reference?: string; quantity?: string } = {},
): BooksField[] => [
  f(id, 'Books id'),
  f(number, 'Document number', { required: true }),
  f(date, 'Date'),
  ...(options.due ? [f(options.due, 'Due / expiry date')] : []),
  f(status, 'Status'),
  f('Customer ID', 'Customer id', { group: 'customer' }),
  f('Customer Name', 'Customer name', { group: 'customer' }),
  f('Currency Code', 'Currency'),
  f('Exchange Rate', 'Exchange rate'),
  f('SubTotal', 'Subtotal'),
  f('Total', 'Total'),
  f('Adjustment', 'Adjustment'),
  f('Entity Discount Amount', 'Document discount'),
  f('Notes', 'Notes'),
  f('Terms & Conditions', 'Terms'),
  ...(options.subject ? [f(options.subject, 'Subject')] : []),
  ...(options.reference ? [f(options.reference, 'Reference / PO')] : []),
  f(options.quantity ?? 'Quantity', 'Line quantity', {
    aliases: options.quantity ? ['Quantity'] : [],
  }),
  ...DOCUMENT_LINE_COLUMNS,
];

export const BOOKS_FIELDS: Record<BooksEntity, readonly BooksField[]> = {
  expenseReports: [
    f('Report Number', 'Report number', { required: true }),
    f('ExpenseReport ID', 'Zoho Expense report id'),
    f('Report Name', 'Title'),
    f('Report Status', 'Status'),
    f('Report Created By Email', 'Claimant email'),
    f('Currency Code', 'Currency'),
    f('Total', 'Report total'),
    f('Business Purpose', 'Notes'),
    f('Created Time', 'Created'),
    f('Submitted Date', 'Submitted on'),
    f('Approved Date', 'Approved on'),
    f('Approver EmailID', 'Approver email'),
    f('Reimbursement Date', 'Reimbursed on'),
    f('Expense ID', 'Expense id'),
    f('Expense Item Date', 'Expense date', { aliases: ['Expense Date'] }),
    f('Expense Category', 'Expense category'),
    f('Expense Description', 'Expense description'),
    f('Merchant Name', 'Merchant'),
    f('Expense Total Amount', 'Expense amount', { aliases: ['Expense Amount'] }),
  ],
  standaloneExpenses: [
    f('Expense Date', 'Date', { required: true }),
    f('Total', 'Total', { group: 'amount' }),
    f('Expense Amount', 'Amount before tax', { group: 'amount' }),
    f('ExpenseReport Name', 'Report (blank = not in a report)'),
    f('Expense Description', 'Description'),
    f('Expense Category', 'Category'),
    f('Merchant Name', 'Merchant'),
    f('Paid Through', 'Paid through'),
    f('Currency Code', 'Currency'),
    f('Reference#', 'Reference'),
    f('Entry Number', 'Entry number'),
    f('Customer Name', 'Customer'),
    f('Email', 'Claimant email'),
  ],
  customers: [
    f('Contact ID', 'Books contact id'),
    f('Reference ID', 'CRM record id (when synced from Zoho CRM)', { aliases: ['Reference Id'] }),
    f('Company Name', 'Company name', { group: 'name' }),
    f('Display Name', 'Display name', { group: 'name' }),
    f('Contact Name', 'Contact name', { group: 'name' }),
    f('Phone', 'Phone'),
    f('MobilePhone', 'Mobile'),
    f('EmailID', 'Email'),
    f('Website', 'Website'),
    f('Notes', 'Notes'),
    f('Currency Code', 'Currency'),
    f('CF.GSTIN', 'GSTIN', { aliases: ['GSTIN', 'GST Number', 'GST No'] }),
    f('CF.Business Manager', 'Business manager', { aliases: ['Business Manager'] }),
    f('Owner Name', 'Owner'),
    f('Created Time', 'Created time'),
    ...ADDRESS_COLUMNS,
    f('Shipping Address', 'Shipping address'),
    f('Shipping Street2', 'Shipping address line 2'),
    f('Shipping City', 'Shipping city'),
    f('Shipping State', 'Shipping state'),
    f('Shipping Code', 'Shipping postal code'),
    f('Shipping Country', 'Shipping country'),
  ],
  contactPersons: [
    f('Reference ID', 'CRM record id (when synced from Zoho CRM)', { aliases: ['Reference Id'] }),
    f('Customer ID', 'Customer id', { group: 'customer' }),
    f('Customer Name', 'Customer name', { group: 'customer' }),
    f('Display Name', 'Display name', { group: 'customer' }),
    f('Contact Person ID', 'Contact person id'),
    f('First Name', 'First name'),
    f('Last Name', 'Last name'),
    f('EmailID', 'Email'),
    f('Phone', 'Phone'),
    f('MobilePhone', 'Mobile'),
    f('Designation', 'Title'),
    f('Department', 'Department'),
  ],
  vendors: [
    f('Contact ID', 'Books contact id'),
    f('Company Name', 'Company name', { group: 'name' }),
    f('Display Name', 'Display name', { group: 'name' }),
    f('Contact Name', 'Contact name', { group: 'name' }),
    f('EmailID', 'Email'),
    f('Phone', 'Phone'),
    f('MobilePhone', 'Mobile'),
    f('Website', 'Website'),
    f('Notes', 'Notes'),
    f('Currency Code', 'Currency'),
    f('Status', 'Status'),
    f('Payment Terms', 'Payment terms (days)'),
    f('CF.GSTIN', 'GSTIN', { aliases: ['GSTIN', 'GST Number'] }),
    f('CF.Business Manager', 'Business manager', { aliases: ['Business Manager'] }),
    f('Owner Name', 'Owner'),
    ...ADDRESS_COLUMNS,
  ],
  items: [
    f('Item ID', 'Books item id'),
    f('Reference ID', 'CRM record id (when synced from Zoho CRM)', { aliases: ['Reference Id'] }),
    f('Item Name', 'Name', { required: true }),
    f('Description', 'Description'),
    f('Rate', 'Price'),
    f('Tax Percentage', 'Tax %'),
    f('Status', 'Status'),
    f('Item Type', 'Type'),
    f('Product Type', 'Product type'),
  ],
  chartOfAccounts: [
    f('Account ID', 'Books account id'),
    f('Account Name', 'Name', { required: true }),
    f('Account Code', 'Code'),
    f('Account Type', 'Type'),
    f('Description', 'Description'),
    f('Account Status', 'Status'),
  ],
  quotes: [
    ...documentFields('Quote ID', 'Quote Number', 'Quote Date', 'Quote Status', {
      due: 'Expiry Date',
      subject: 'Subject',
      reference: 'PurchaseOrder',
    }),
    f('Reference ID', 'CRM record id (when synced from Zoho CRM)', { aliases: ['Reference Id'] }),
    f('CF.Service Type', 'Service type', { aliases: ['Service Type'] }),
    f('CF.Event Date', 'Event date', { aliases: ['Event Date'] }),
    f('CF.Event Location', 'Event location', { aliases: ['Event Location'] }),
    f('CF.Client Type', 'Client type', { aliases: ['Client Type'] }),
    f('CF.High Flyer Clients', 'High flyer', { aliases: ['High Flyer Clients'] }),
    f('CF.Business Development Manager', 'Business manager', {
      aliases: ['Business Development Manager'],
    }),
  ],
  salesOrders: documentFields('SalesOrder ID', 'SalesOrder Number', 'Order Date', 'Status', {
    due: 'Expected Shipment Date',
    reference: 'Reference#',
    quantity: 'QuantityOrdered',
  }),
  invoices: [
    ...documentFields('Invoice ID', 'Invoice Number', 'Invoice Date', 'Invoice Status', {
      due: 'Due Date',
      subject: 'Subject',
      reference: 'PurchaseOrder',
    }),
    f('Estimate Number', 'Quote it was raised from', { aliases: ['Quote Number'] }),
    f('CF.Business Development Manager', 'Business manager', {
      aliases: ['Business Development Manager'],
    }),
  ],
  customerPayments: [
    f('Payment Number', 'Payment number', { required: true }),
    f('CustomerPayment ID', 'Books payment id'),
    f('Invoice Number', 'Invoice number', { required: true }),
    f('Amount Applied to Invoice', 'Amount applied', { required: true }),
    f('Invoice Payment Applied Date', 'Applied on'),
    f('InvoicePayment ID', 'Applied-payment id'),
    f('Date', 'Payment date'),
    f('Mode', 'Payment mode'),
    f('Reference Number', 'Reference'),
    f('Currency Code', 'Currency'),
    f('Deposit To', 'Deposited to'),
    f('Description', 'Notes'),
  ],
  creditNotes: [
    ...documentFields(
      'CreditNotes ID',
      'Credit Note Number',
      'Credit Note Date',
      'Credit Note Status',
      {
        subject: 'Subject',
        reference: 'Reference#',
      },
    ),
  ],
  creditNoteApplications: [
    f('Credit Note Number', 'Credit note number', { required: true }),
    f('Invoice Number', 'Invoice number', { required: true }),
    f('Amount', 'Amount', { required: true }),
    f('Applied Invoice Date', 'Applied on'),
    f('Date', 'Date'),
  ],
  creditNoteRefunds: [
    f('Credit Note Number', 'Credit note number', { required: true }),
    f('Amount', 'Amount', { required: true }),
    f('Date', 'Refund date'),
    f('Mode of Refund', 'Refund mode'),
    f('Reference Number', 'Reference'),
    f('Description', 'Notes'),
  ],
  expenses: [
    f('Expense Date', 'Date', { required: true }),
    f('Total', 'Total', { group: 'amount' }),
    f('Expense Amount', 'Amount before tax', { group: 'amount' }),
    f('Expense Description', 'Description'),
    f('Expense Account', 'Expense account'),
    f('Paid Through', 'Paid through'),
    f('Vendor', 'Vendor'),
    f('Currency Code', 'Currency'),
    f('Reference#', 'Reference'),
    f('Entry Number', 'Entry number'),
    f('Customer Name', 'Customer'),
    f('Claimant Email', 'Claimant email'),
    f('ExpenseReport Name', 'Expense report'),
  ],
  journals: [
    f('Journal Number', 'Journal number', { required: true }),
    f('Journal ID', 'Books journal id'),
    f('Journal Date', 'Date'),
    f('Status', 'Status'),
    f('Reference Number', 'Reference'),
    f('Notes', 'Notes'),
    f('Account', 'Account', { required: true }),
    f('Debit', 'Debit', { required: true }),
    f('Credit', 'Credit', { required: true }),
    f('Contact Name', 'Contact'),
    f('Contact Type', 'Contact type'),
    f('Description', 'Line description'),
  ],
};

/** Case, spacing and punctuation never decide whether two headers match. */
const squash = (value: string): string => value.toLowerCase().replace(/[^a-z0-9%#]+/gu, '');

export interface BooksColumnStatus {
  column: string;
  label: string;
  required: boolean;
  group: string | null;
  /** The file column feeding it now (an override, else the auto-match), or null. */
  source: string | null;
  /** True when the user chose it, not the auto-matcher. */
  overridden: boolean;
}

export interface BooksMappingStatus {
  columns: BooksColumnStatus[];
  /** File columns nothing reads. They are not imported. */
  unusedHeaders: string[];
  /** What blocks the entity: required fields and name/customer groups with nothing mapped. */
  missingRequired: string[];
}

/**
 * What each importer column would read from this file, given any overrides.
 *
 * Exact header first, then case/spacing-insensitive, then the aliases — so a
 * file exported with a slightly different spelling still maps without help.
 */
export function booksMappingStatus(
  entity: BooksEntity,
  headers: readonly string[],
  overrides: Record<string, string> = {},
): BooksMappingStatus {
  const lookup = new Map<string, string>();
  for (const header of headers) {
    if (!lookup.has(squash(header))) lookup.set(squash(header), header);
  }
  const exact = new Set(headers);

  const columns = BOOKS_FIELDS[entity].map((field): BooksColumnStatus => {
    const override = overrides[field.column];
    const chosen = override !== undefined && (override === '' || exact.has(override));

    let source: string | null = null;
    if (chosen) {
      source = override === '' ? null : override!;
    } else if (exact.has(field.column)) {
      source = field.column;
    } else {
      for (const name of [field.column, ...(field.aliases ?? [])]) {
        const hit = lookup.get(squash(name));
        if (hit) {
          source = hit;
          break;
        }
      }
    }

    return {
      column: field.column,
      label: field.label,
      required: field.required ?? false,
      group: field.group ?? null,
      source,
      overridden: chosen,
    };
  });

  const missingRequired = columns.filter((c) => c.required && !c.source).map((c) => c.label);
  const groups = new Set(columns.map((c) => c.group).filter((g): g is string => g !== null));
  for (const group of groups) {
    const members = columns.filter((c) => c.group === group);
    if (members.every((c) => !c.source)) {
      missingRequired.push(members.map((c) => c.label).join(' or '));
    }
  }

  const used = new Set(columns.map((c) => c.source).filter((s): s is string => s !== null));
  return {
    columns,
    unusedHeaders: headers.filter((header) => !used.has(header)),
    missingRequired,
  };
}

/**
 * Renames the file's columns to the names the importer reads.
 *
 * The chosen file column is copied onto the importer's name; the original is
 * left in place, so a column mapped twice (or read under its own name by
 * another field) still works.
 */
export function applyBooksMapping<T extends Record<string, string>>(
  entity: BooksEntity,
  rows: readonly T[],
  headers: readonly string[],
  overrides: Record<string, string> = {},
): T[] {
  const status = booksMappingStatus(entity, headers, overrides);
  // A column the file lacks and the user did not touch has nothing to copy or blank.
  const renames = status.columns.filter(
    (c) => c.source !== c.column && (c.source !== null || c.overridden),
  );
  if (renames.length === 0) return [...rows];

  return rows.map((row) => {
    const next: Record<string, string> = { ...row };
    for (const column of renames) {
      next[column.column] = column.source ? (row[column.source] ?? '') : '';
    }
    return next as T;
  });
}
